import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { CircularProgress, Box } from '@mui/material';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { NURSE_LANDING_PATH, isNurseBlockedPath } from '../../constants/nurseScope';
import { useNurseScope } from '../../hooks/useNurseScope';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[]; // Auth0 roles (legacy)
  requiredPermissions?: string[]; // Auth0 permissions (legacy)
  requiredCustomRoles?: string[]; // Database custom roles (NEW, opt-in)
  requiredCustomPermissions?: string[]; // Database custom permissions (NEW, opt-in)
  allowKioskUsers?: boolean; // Allow kiosk users on this route (NEW)
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  requiredCustomRoles,
  requiredCustomPermissions,
  allowKioskUsers = false,
}) => {
  const {
    isAuthenticated,
    isLoading,
    hasRole,
    hasPermission,
    hasCustomRole,
    hasCustomPermission,
    isKioskOnly,
  } = useAuth();
  const { activePharmacy } = usePharmacyStore();
  const { isNurse } = useNurseScope();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // KIOSK USER AUTO-REDIRECT: Block kiosk users from non-kiosk routes
  if (isKioskOnly() && !allowKioskUsers) {
    return <Navigate to={`/kiosk?pharmacy=${activePharmacy?.id}&mode=driver-first`} replace />;
  }

  // NURSE SCOPE (GP-1183): filtering the sidebar is not enough — a nurse must
  // not reach a blocked queue by typing its URL either. Blocked paths land on
  // Delivery Status (the one operations screen the role keeps) rather than
  // /unauthorized, so a nurse whose saved default landing page is a blocked
  // queue still gets a working screen instead of a dead end.
  if (isNurse && isNurseBlockedPath(location.pathname)) {
    return <Navigate to={NURSE_LANDING_PATH} replace />;
  }

  // Check Auth0 required roles (LEGACY - kept for backward compatibility)
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check Auth0 required permissions (LEGACY - kept for backward compatibility)
  if (requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.every(permission => hasPermission(permission));
    if (!hasRequiredPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check custom role requirements (NEW - database-backed, OPT-IN only)
  if (requiredCustomRoles && requiredCustomRoles.length > 0) {
    const hasRequiredCustomRole = requiredCustomRoles.some(role => hasCustomRole(role));
    if (!hasRequiredCustomRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check custom permission requirements (NEW - database-backed, OPT-IN only)
  // User must have ALL required custom permissions
  if (requiredCustomPermissions && requiredCustomPermissions.length > 0) {
    const hasAllCustomPermissions = requiredCustomPermissions.every(permission =>
      hasCustomPermission(permission)
    );
    if (!hasAllCustomPermissions) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};